import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { createV2App, listV2Flows, studioV2NewAppDraft } from '../../../api/studioV2.api';
import { STUDIO_V2_APP_TEMPLATES } from '../studioV2AppTemplates';
import { buildAppDefinitionForCreate } from '../studioV2StarterHelpers';
import { THEMES } from '../../Dashboard/AppThemeProvider';
import {
  layoutThumbnail, setChatFlow, setDetails, setPanelFlow, setTheme,
} from './appBuilderModel';
import { LayoutSketch } from './panelVisuals';

const FRIENDLY_TEMPLATES = {
  'permit-advisor': { label: 'SF application advisor', description: 'Studio App: permit vs planning vs license with checklist and summary.' },
  'intake-assistant': { label: 'Intake assistant', description: 'Collect details with a form while the assistant takes notes.' },
  'customer-support': { label: 'Customer support', description: 'A case list, an assistant and key numbers for support teams.' },
  'lead-qualification': { label: 'Lead qualification', description: 'Score leads and suggest next steps from the conversation.' },
  'candidate-screening-console': { label: 'Candidate screening', description: 'Compare candidates in a table and chat about them.' },
  'interview-scorecard-console': { label: 'Interview scorecard', description: 'Score interviews by topic with a manager summary.' },
  'recruiting-pilot-console': { label: 'Recruiting pilot', description: 'Screening and scorecards in one app with a shared chat.' },
  'three-support': { label: 'Table, chat and summary', description: 'A classic three-column workspace.' },
  'two-data': { label: 'Chart and chat', description: 'A chart next to the assistant.' },
  'chat-only': { label: 'Just a chat', description: 'The quickest way to put a flow in front of people.' },
};

const STEPS = ['Start', 'Details', 'Look'];

function blankDefinition() {
  return studioV2NewAppDraft('Untitled app');
}

function StartCard({
  selected, onClick, title, description, sketch, testId,
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testId}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'rgba(37,99,235,.04)' : 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        gap: 1.25,
        height: '100%',
      }}
    >
      {sketch}
      <Box>
        <Typography variant="body2" fontWeight={700}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{description}</Typography>
      </Box>
    </ButtonBase>
  );
}

/** Guided "New app": pick a starting point, name it, connect the AI, choose colors, create. */
export default function NewAppWizard({
  open, onClose, onCreated, initialTemplateId = null,
}) {
  const [step, setStep] = useState(0);
  const [startId, setStartId] = useState('blank');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chatFlowId, setChatFlowId] = useState('');
  const [fillPanels, setFillPanels] = useState(false);
  const [themeId, setThemeId] = useState('default');
  const [flows, setFlows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setStartId(initialTemplateId || 'blank');
    setName('');
    setDescription('');
    setChatFlowId('');
    setFillPanels(false);
    setThemeId('default');
    setError(null);
    listV2Flows().then(({ flows: rows }) => setFlows(rows || []));
  }, [open, initialTemplateId]);

  const template = STUDIO_V2_APP_TEMPLATES.find((t) => t.id === startId) || null;
  const baseDef = useMemo(
    () => (template ? buildAppDefinitionForCreate(template) : blankDefinition()),
    [template],
  );
  const hasChat = baseDef.panels?.some((p) => p.type === 'chat');
  const dataPanels = (baseDef.panels || []).filter((p) => p.type === 'component' && !p.source?.flowRef);
  const defaultName = template ? (FRIENDLY_TEMPLATES[template.id]?.label || template.label) : 'Untitled app';

  const buildDefinition = () => {
    let def = setDetails(baseDef, {
      name: name.trim() || defaultName,
      description: description.trim() || (template ? baseDef.description : ''),
    });
    def = setTheme(def, { themeId });
    if (chatFlowId) {
      if (hasChat) def = setChatFlow(def, chatFlowId);
      if (fillPanels || !hasChat) dataPanels.forEach((p) => { def = setPanelFlow(def, p.id, chatFlowId); });
    }
    return def;
  };

  const onCreate = async () => {
    setCreating(true);
    setError(null);
    const { app, error: err } = await createV2App(buildDefinition());
    setCreating(false);
    if (err || !app?._id) {
      setError(err || 'The app could not be created.');
      return;
    }
    onCreated(app);
  };

  const accent = THEMES[themeId]?.primary || '#2563eb';

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="md" fullWidth data-testid="new-app-wizard">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={700}>Create an app</Typography>
        <Stepper activeStep={step} sx={{ mt: 2 }}>
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 380 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, pt: 1 }}>
            <StartCard
              selected={startId === 'blank'}
              onClick={() => setStartId('blank')}
              title="Blank app"
              description="Start with just a chat and add panels yourself."
              testId="wizard-start-blank"
              sketch={(
                <Box sx={{
                  height: 52, borderRadius: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary',
                }}
                >
                  <AddIcon />
                </Box>
              )}
            />
            {STUDIO_V2_APP_TEMPLATES.map((t) => (
              <StartCard
                key={t.id}
                selected={startId === t.id}
                onClick={() => setStartId(t.id)}
                title={FRIENDLY_TEMPLATES[t.id]?.label || t.label}
                description={FRIENDLY_TEMPLATES[t.id]?.description || t.description}
                testId={`wizard-start-${t.id}`}
                sketch={<LayoutSketch columns={layoutThumbnail(t.definition)} height={52} />}
              />
            ))}
          </Box>
        )}

        {step === 1 && (
          <Stack spacing={2.5} sx={{ pt: 1, maxWidth: 560 }}>
            <TextField
              label="App name"
              autoFocus
              fullWidth
              placeholder={defaultName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ 'data-testid': 'wizard-name' }}
            />
            <TextField
              label="What is it for? (optional)"
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>Connect your AI</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                {hasChat
                  ? 'Pick the flow that answers questions in the chat. You can change this later.'
                  : 'Pick a flow to fill the panels. You can change this later.'}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Flow</InputLabel>
                <Select label="Flow" value={chatFlowId} onChange={(e) => setChatFlowId(e.target.value)} data-testid="wizard-flow">
                  <MenuItem value=""><em>Decide later</em></MenuItem>
                  {flows.map((f) => (
                    <MenuItem key={f._id} value={String(f._id)}>{f.name || f.definition?.name || 'Untitled flow'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {chatFlowId && dataPanels.length > 0 && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Checkbox size="small" checked={fillPanels || !hasChat} disabled={!hasChat} onChange={(e) => setFillPanels(e.target.checked)} />}
                  label={(
                    <Typography variant="body2">
                      {`Also use it for the ${dataPanels.length} data panel${dataPanels.length === 1 ? '' : 's'}`}
                    </Typography>
                  )}
                />
              )}
              {!flows.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  You have no flows yet. Create the app now and connect one later.
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">Pick colors. Everything else can be tuned later under Look &amp; feel.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1.25 }}>
              {Object.entries(THEMES).map(([id, t]) => (
                <ButtonBase
                  key={id}
                  onClick={() => setThemeId(id)}
                  aria-pressed={themeId === id}
                  data-testid={`wizard-theme-${id}`}
                  sx={{
                    p: 1.25, borderRadius: 2.5, border: 2, borderColor: themeId === id ? 'primary.main' : 'divider', display: 'flex', flexDirection: 'column', gap: 1,
                  }}
                >
                  <Box sx={{
                    display: 'flex', width: '100%', height: 34, borderRadius: 1.5, overflow: 'hidden', position: 'relative',
                  }}
                  >
                    <Box sx={{ flex: 2, bgcolor: t.primary }} />
                    <Box sx={{ flex: 1, bgcolor: t.secondary }} />
                    <Box sx={{ flex: 1, bgcolor: t.accent }} />
                    {themeId === id && <CheckIcon sx={{ position: 'absolute', left: 8, top: 7, color: '#fff' }} />}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                </ButtonBase>
              ))}
            </Box>
            <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">Your app</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: accent }}>{name.trim() || defaultName}</Typography>
              <Box sx={{ mt: 1 }}>
                <LayoutSketch columns={layoutThumbnail(baseDef)} accent={accent} height={64} />
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={creating} sx={{ textTransform: 'none', mr: 'auto' }}>Cancel</Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={creating} sx={{ textTransform: 'none' }}>Back</Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} sx={{ textTransform: 'none', fontWeight: 600 }} data-testid="wizard-next">
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            data-testid="wizard-create"
          >
            Create app
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
